import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {LoadingButton} from "@mui/lab";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Stack,
    Tooltip
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {Survey, SurveyStatus} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {ElementChip} from "../../common/uiElements/base/ElementChip";
import {getSurveyStatusColor} from "../../common/uiElements/colors";
import {useSurveyChangeRefresh} from "../shared/surveyQueryRefresh";
import SurveyDetailsSection from "./SurveyDetailsSection";
import SurveyPublishDialog from "./SurveyPublishDialog";

interface Props {
    surveyData: UseEntityData<Survey>
}

const SurveyPublishSection = ({surveyData}: Props) => {

    const {t} = useTranslation()
    const readOnly = useReadOnly()
    const refreshSurveys = useSurveyChangeRefresh()

    const [publishDialogOpen, setPublishDialogOpen] = useState(false)
    const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false)
    const [unpublishing, setUnpublishing] = useState(false)

    const survey = surveyData.item
    const surveyId = survey.id

    const publishInfoQuery = useQuery(
        ['surveyPublishInfo', surveyId, String(survey.updateDate ?? '')],
        () => SurveyService.publishInfo({id: surveyId!}),
        {enabled: !!surveyId, refetchOnWindowFocus: false}
    )
    const publishInfo = publishInfoQuery.data

    const published = survey.status === SurveyStatus.PUBLISHED
    const liveVersion = publishInfo?.version ?? survey.version ?? 0
    const hasUnpublishedChanges = !!publishInfo?.hasUnpublishedChanges
    const publishedAt = publishInfo?.publishedAt ?? survey.publishedAt

    const inSync = published && !hasUnpublishedChanges
    const publishLabel = published ? t('Publish changes') : t('Publish')

    // Publish and unpublish both end here.
    const refreshAfterPublish = async () => {
        void refreshSurveys()
        await Promise.all([surveyData.fetch(), publishInfoQuery.refetch()])
    }

    const unpublish = async () => {
        if (!surveyId) return

        setUnpublishing(true)
        try {
            await SurveyService.unpublishSurvey(surveyId)
            setUnpublishConfirmOpen(false)
            await refreshAfterPublish()
            enqueueSnackbar(t('Survey unpublished'), {
                variant: 'success',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            })
        } catch (e) {
            console.log(e)
            enqueueSnackbar(t('The survey could not be unpublished.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            })
        } finally {
            setUnpublishing(false)
        }
    }

    const statusChip = (
        <Box sx={{display: 'inline-flex', maxWidth: '100%'}}>
            <ElementChip
                text={published ? t('Published · v{{n}}', {n: liveVersion}) : t('Draft')}
                startIcon={
                    <Box
                        component={'span'}
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: getSurveyStatusColor(survey.status),
                            flex: '0 0 auto',
                        }}
                    />
                }
            />
        </Box>
    )

    const metaLine = published
        ? [
            publishedAt ? t('Published {{date}}', {date: formatDateDMYHM(publishedAt)}) : undefined,
            t('{{count}} submissions on v{{n}}', {
                count: publishInfo?.submissionCountOnLiveVersion ?? 0,
                n: liveVersion
            })
        ].filter(Boolean).join(' · ')
        : undefined

    const blockedByUnsavedChanges = surveyData.isDirty
    const publishDisabled = readOnly || inSync || blockedByUnsavedChanges

    const publishButton = (
        <Tooltip title={blockedByUnsavedChanges ? t('Save your changes first') : ''}>
            <span>
                <Button
                    variant={'outlined'}
                    startIcon={<PublishedWithChangesOutlinedIcon/>}
                    onClick={() => setPublishDialogOpen(true)}
                    disabled={publishDisabled}
                    sx={{flexShrink: 0}}
                >
                    {publishLabel}
                </Button>
            </span>
        </Tooltip>
    )

    return (
        <SurveyDetailsSection
            title={t('Publish')}
            actions={<Box sx={{flexShrink: 0}}>{publishButton}</Box>}
        >
            <Paper variant={'outlined'} sx={{p: 2.5, borderRadius: 2, width: '100%', minWidth: 0}}>
                <Stack gap={1.25}>
                    <Stack direction={'row'} alignItems={'center'} gap={1.5} flexWrap={'wrap'}>
                        {statusChip}
                        {!!metaLine && (
                            <Typography variant={'body2'} color={'text.secondary'} sx={{minWidth: 0}}>
                                {metaLine}
                            </Typography>
                        )}

                        {published && (
                            <>
                                <Box sx={{flex: 1}}/>
                                <Button
                                    size={'small'}
                                    color={'error'}
                                    disabled={readOnly}
                                    onClick={() => setUnpublishConfirmOpen(true)}
                                    sx={{textTransform: 'none', flexShrink: 0}}
                                >
                                    {t('Unpublish')}
                                </Button>
                            </>
                        )}
                    </Stack>

                    {published && hasUnpublishedChanges && (
                        <Alert severity={'warning'} sx={{width: '100%'}}>
                            {t('Unpublished changes - guests still see v{{n}}', {n: liveVersion})}
                        </Alert>
                    )}

                </Stack>
            </Paper>

            {publishDialogOpen && (
                <SurveyPublishDialog
                    open
                    survey={survey}
                    onClose={() => setPublishDialogOpen(false)}
                    onPublished={refreshAfterPublish}
                />
            )}

            <Dialog
                open={unpublishConfirmOpen}
                onClose={() => setUnpublishConfirmOpen(false)}
                maxWidth={'xs'}
                fullWidth
                aria-labelledby={'unpublish-survey-dialog-title'}
                aria-describedby={'unpublish-survey-dialog-description'}
            >
                <DialogTitle
                    id={'unpublish-survey-dialog-title'}
                    sx={{display: 'flex', alignItems: 'center', gap: 1}}
                >
                    <WarningAmberIcon color={'warning'}/>
                    {t('Unpublish survey?')}
                </DialogTitle>

                <DialogContent>
                    <DialogContentText id={'unpublish-survey-dialog-description'}>
                        {t('Guests can no longer open this survey. Links stay valid and start working again as soon as you publish.')}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant={'outlined'}
                        onClick={() => setUnpublishConfirmOpen(false)}
                        disabled={unpublishing}
                    >
                        {t('Cancel')}
                    </Button>

                    <LoadingButton
                        variant={'contained'}
                        color={'error'}
                        onClick={unpublish}
                        loading={unpublishing}
                    >
                        {t('Unpublish')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </SurveyDetailsSection>
    )
}

export default SurveyPublishSection
